import React, { useMemo, useState } from "react";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Progress } from "@shared/ui/progress";
import { Switch } from "@shared/ui/switch";
import {
  Eye,
  Edit,
  ToggleLeft,
  ToggleRight,
  FileText,
  Calendar,
  User,
  Hash,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
} from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Template, sopFilters } from "../../types";
import {
  FieldTooltip,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { useCurrency } from "@shared/selectors/globalSelectors";

interface SopTemplatesTableProps {
  templates: Template[];
  isDraftsView?: boolean;
  onViewDetails: (templateId: string, templateName: string) => void;
  onEditTemplate: (template: Template) => void;
  onClone: (template: Template) => void;
  onStatusToggle: (templateId: string, isActive: boolean) => void;
  emptyMessage?: string;
  loading?: boolean;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    startItem: number;
    endItem: number;
  };
  onPageChange?: (page: number) => void;
  sortConfig?: {
    field: string;
    direction: "asc" | "desc";
  };
  onSort?: (field: string, direction: "asc" | "desc") => void;
  selectable?: boolean;
  selectedRows?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  bulkActions?: Array<{
    label: string;
    onClick: (selectedData: Template[], selectedIds: string[]) => void;
    icon?: React.ReactNode;
    variant?: "default" | "destructive" | "outline";
  }>;

  // New props for search and filtering like UserTable
  onSearch?: (searchTerm: string, paramName: string) => void;
  searchPlaceholder?: string;
  searchParamName?: string;
  searchDebounceMs?: number;
  currentFilters?: Partial<sopFilters>;
  onApplyFilters?: (filters: Partial<sopFilters>) => void;
  onResetFilters?: () => void;
  filterPanel?: React.ReactNode;
}

const SopTemplatesTable: React.FC<SopTemplatesTableProps> = ({
  templates,
  isDraftsView = false,
  onViewDetails,
  onEditTemplate,
  onClone,
  onStatusToggle,
  emptyMessage,
  loading = false,
  pagination,
  onPageChange,
  sortConfig,
  onSort,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  bulkActions = [],

  // Search and filter props
  onSearch,
  searchPlaceholder,
  searchParamName = "query",
  searchDebounceMs = 300,
  currentFilters,
  onApplyFilters,
  onResetFilters,
  filterPanel,
}) => {
  const currency = useCurrency();
  const defaultEmptyMessage = isDraftsView
    ? "No draft templates found matching your search and filters."
    : "No templates found matching your search and filters.";

  const defaultSearchPlaceholder = isDraftsView
    ? "Search draft templates..."
    : "Search SOP templates...";

  // Helper function to get status color and variant
  const getStatusConfig = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return {
          variant: "default" as const,
          className: "bg-green-100 text-green-800 border-green-200",
        };
      case "inactive":
        return {
          variant: "secondary" as const,
          className: "bg-gray-100 text-gray-800 border-gray-200",
        };
      case "draft":
        return {
          variant: "outline" as const,
          className: "bg-yellow-100 text-yellow-800 border-yellow-200",
        };
      case "archived":
        return {
          variant: "destructive" as const,
          className: "bg-red-100 text-red-800 border-red-200",
        };
      default:
        return { variant: "default" as const, className: "" };
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (template: Template, checked: boolean) => {
    onStatusToggle(template.id, checked);
  };

  // Column configuration
  const columns: TableColumn<Template>[] = useMemo(
    () => [
      {
        key: "id",
        header: "Template ID",
        sortable: true,
        width: "w-[100px]",
        render: (value: string) => (
          <div>
            <span className="font-mono text-sm">{value}</span>
          </div>
        ),
      },
      {
        key: "name",
        header: "Template Name",
        sortable: true,
        width: "min-w-[200px]",
        render: (value: string, row: Template) => (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{value}</span>
            </div>
            {row.description && (
              <p className="text-xs text-muted-foreground line-clamp-2 max-w-[250px]">
                {row.description}
              </p>
            )}
          </div>
        ),
      },
      {
        key: "association",
        header: "Association",
        sortable: true,
        width: "w-32",
        render: (value: string) => <span className="text-sm">{value}</span>,
      },
      {
        key: "type",
        header: "Type",
        sortable: true,
        width: "w-32",
        render: (value: string) => <span className="text-sm">{value}</span>,
      },
      {
        key: "subtype",
        header: "Subtype",
        sortable: true,
        width: "w-32",
        render: (value: string) => <span className="text-sm">{value}</span>,
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        width: "w-32",
        render: (value: string, row: Template) => (
          <div className="flex items-center gap-2">
            <Badge
              variant={getStatusConfig(value).variant}
              className={getStatusConfig(value).className}
            >
              {value}
            </Badge>
          </div>
        ),
      },

      {
        key: "version",
        header: "Version",
        sortable: true,
        width: "w-24",
        render: (value: string) => (
          <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
            v{value}
          </span>
        ),
      },
      {
        key: "createdAt",
        header: "Created",
        sortable: true,
        width: "w-32",
        render: (value: string) => (
          <div className="flex items-center gap-2">
            <Calendar className="h-3 w-3 text-muted-foreground" />
            <span className="text-sm">{value}</span>
          </div>
        ),
      },
      {
        key: "createdBy",
        header: "Created By",
        sortable: true,
        width: "w-32",
        render: (value: string) => (
          <div className="flex items-center gap-2">
            <User className="h-3 w-3 text-muted-foreground" />
            <span className="text-sm">{value}</span>
          </div>
        ),
      },
      {
        key: "taskCount",
        header: "Tasks",
        sortable: true,
        width: "w-20",
        render: (value: number) => (
          <div className="flex items-center gap-2">
            <CheckCircle className="h-3 w-3 text-green-600" />
            <span className="font-medium">{value}</span>
          </div>
        ),
      },
      {
        key: "usageCount",
        header: "Usage Count",
        sortable: true,
        width: "w-28",
        render: (value: number) => (
          <div className="flex items-center gap-2">
            <span className="font-medium text-blue-600">{value}</span>
            <span className="text-xs text-muted-foreground">times</span>
          </div>
        ),
      },
      {
        key: "estTotalCost",
        header: "Total Cost",
        sortable: true,
        width: "w-28",
        render: (value: string) => (
          <div className="flex items-center gap-2">
            <span className="font-medium text-green-600">{currency}{value}</span>
          </div>
        ),
      },
      // {
      //   key: "sla",
      //   header: "SLA (hrs)",
      //   sortable: true,
      //   width: "min-w-[120px]",
      //   render: (value: { value: string; progress: number }) => (
      //     <div className="space-y-1">
      //       <div className="flex items-center gap-2">
      //         <Clock className="h-3 w-3 text-muted-foreground" />
      //         <span className="text-sm font-medium">{value.value}</span>
      //       </div>
      //     </div>
      //   ),
      // },
      {
        key: "isActive",
        header: "Active Status",
        sortable: true,
        width: "w-32",
        render: (value: boolean, row: Template) => {
          // Debug logging - remove after fixing
          console.log(
            `Template ${row.id}: isActive = ${row.isActive}, value = ${value}`
          );

          return (
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`template-${row.id}`}
                        checked={row.isActive}
                        onCheckedChange={(checked) =>
                          handleToggleStatus(row, checked)
                        }
                      />
                    </div>
                  </TooltipTrigger>
                  <FieldTooltip>
                    {row.isActive ? "Deactivate" : "Activate"} Template
                  </FieldTooltip>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        },
      },
    ],
    [onStatusToggle]
  );

  // Actions configuration - updated to remove status toggle actions since we now have the column
  const actions = useMemo(
    () => [
      {
        label: "View Details",
        onClick: (template: Template, index: number) =>
          onViewDetails(template.id, template.name),
        icon: "Eye" as const,
        variant: "ghost" as const,
      },
      {
        label: "Edit Template",
        onClick: (template: Template, index: number) =>
          onEditTemplate(template),
        icon: "LocationEdit" as const,
        variant: "ghost" as const,
      },
      {
        label: "Clone",
        onClick: (template: Template, index: number) => onClone(template),
        icon: "Copy" as const,
        variant: "ghost" as const,
      },
    ],
    [onViewDetails, onEditTemplate, onClone]
  );

  return (
    <GenericTable
      data={templates}
      columns={columns}
      rowKey="id"
      loading={loading}
      // Search integration like UserTable
      searchPlaceholder={searchPlaceholder || defaultSearchPlaceholder}
      searchParamName={searchParamName}
      searchDebounceMs={searchDebounceMs}
      onSearch={onSearch}
      // Filter panel integration like UserTable
      filterPanel={filterPanel}
      // Selection and bulk actions
      selectable={selectable}
      selectedRows={selectedRows}
      onSelectionChange={onSelectionChange}
      bulkActions={bulkActions}
      // Actions
      actions={actions}
      actionsType="dropdown"
      // Pagination and sorting
      pagination={pagination}
      onPageChange={onPageChange}
      sortConfig={sortConfig}
      onSort={onSort}
      // Empty state
      emptyMessage={emptyMessage || defaultEmptyMessage}
      emptyIcon={<FileText className="h-8 w-8 text-muted-foreground" />}
      getRowClassName={(template) =>
        template.status === "inactive" ? "opacity-60" : ""
      }
    />
  );
};

export default SopTemplatesTable;
